<script setup lang="ts">
    import { type ColumnDefinition } from "@cmnhospitals/components-vue";
    import { CmnhDataGrid } from "@cmnhospitals/components-vue/datagrid";
    import {
        currencyFormatter,
        dateFormatter,
        officeChipsFormatter,
        statusFormatter
    } from "@/composables/tabulatorFormatters";
    import { ref, onMounted, watch, onUnmounted, computed } from "vue";
    import { DateTime } from "luxon";
    import Spinner from '@/components/common/Spinner.vue';
    import Alert from '@/components/common/Alert.vue';
    import { SupportEmail } from '@/constants';

    const props = defineProps<{
        rowData: string // Supports API URL (string)
        search?: string; // Filter value from PaymentsSearchForm
        searchFilters?: Record<string, any>; // Ensure searchFilters is defined
        isSearching?: boolean;
    }>();

    const columns: ColumnDefinition[] = [
        {
            title: "Date Submitted",
            field: "dateSubmitted",
            headerSort: true,
            formatter: dateFormatter,
            minWidth: 165,
            sorter: (a: any, b: any) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis(),
        },
        {
            title: "Payment #",
            field: "transactionNumber",
            headerSort: true,
            minWidth: 120,
            formatter: (cell: any) => {
                const rowData = cell.getRow().getData();
                const transactionId = rowData.transactionId;
                const transactionNumber = rowData.transactionNumber ?? "N/A";

                return `<a href="/transactions/details/${transactionId}">${transactionNumber}</a>`;
            }
        },
        {
            title: "Payment Type",
            field: "paymentMethodType",
            headerSort: true,
            minWidth: 140
        },
        {
            title: "Office(s)",
            field: "offices",
            headerSort: true,
            formatter: officeChipsFormatter,
            minWidth: 150
        },
        {
            title: "Submitted",
            field: "amountSubmitted",
            headerSort: true,
            formatter: currencyFormatter,
            minWidth: 110
        },
        {
            title: "Refunded",
            field: "amountRefunded",
            headerSort: true,
            formatter: currencyFormatter,
            minWidth: 100
        },
        {
            title: "New Total",
            field: "amountReceived",
            headerSort: true,
            formatter: currencyFormatter,
            minWidth: 100
        },
        {
            title: "Status",
            field: "transactionStatus",
            headerSort: true,
            formatter: (cell: any) => {
                const { transactionStatus, transactionId } = cell.getRow().getData();
                const statusHtml = statusFormatter(cell);
                if (transactionStatus?.toLowerCase() === 'confirmed - needs review' && transactionId) {
                    return `${statusHtml}<br><a class="btn btn-reconcile btn-xs reconcile-btn" href="/transactions/reconcile/${encodeURIComponent(transactionId)}">Reconcile</a>`;
                }
                return statusHtml;
            },
            variableHeight: true,
            minWidth: 120
        }
    ];

    const emit = defineEmits(["set-grid-ref", "search-complete", "record-count"]);

    const gridRef = ref(null);
    const hasData = ref(true);
    const isLoading = ref(true);
    const fetchError = ref<string | null>(null);
    const recordCount = ref<number | null>(null);
    const lastRowData = ref(props.rowData);
    const rowDataRef = ref(props.rowData); // This is the one you'll bind to CmnhDataGrid


    const spinnerLabel = computed(() => {
        if (recordCount.value === null) return "Fetching your transactions...";

        // Format recordCount with commas
        const formattedCount = new Intl.NumberFormat("en-US").format(recordCount.value);

        return `Found ${formattedCount} transaction${recordCount.value === 1 ? "" : "s"}.`;
    });

    function handleDataLoaded() {
        if (!props.isSearching) return; // Ignore pagination updates

        isLoading.value = true;

        setTimeout(() => {
            const paginationInfo = document.querySelectorAll(".pagination-info span");

            if (paginationInfo.length > 1) {
                const count = parseInt(paginationInfo[1].textContent?.trim() || "0", 10);
                hasData.value = count > 0;
                recordCount.value = count;
                fetchError.value = null; // Reset error state
                emit("record-count", count);  // Always update total count

                setTimeout(() => {
                    isLoading.value = false;
                    recordCount.value = null;
                    emit("search-complete"); // Tell parent to reset isSearching
                }, 1500);
            } else {
                hasData.value = false;
                isLoading.value = false;
                emit("record-count", 0); // No records found
                emit("search-complete"); // Ensure reset on empty results
            }
        }, 300);
    }

    function retryLastSearch() {
        fetchError.value = null;
        isLoading.value = true;
        rowDataRef.value = ""; // Trigger a reset
        requestAnimationFrame(() => {
            rowDataRef.value = lastRowData.value; // Reassign to force reload
        });
    }

    watch(() => props.rowData, (newVal) => {
        isLoading.value = true;
        hasData.value = true;
        lastRowData.value = newVal;
        rowDataRef.value = newVal;
    });


    onMounted(() => {
        if (gridRef.value) {
            emit("set-grid-ref", gridRef.value);
        } else {
            console.warn("Grid reference is not set on mount.");
        }

        // Observe changes in the pagination info element
        const paginationElement = document.querySelector(".pagination-info");

        if (paginationElement) {
            const paginationObserver = new MutationObserver(() => {
                handleDataLoaded();
            });

            paginationObserver.observe(paginationElement, { childList: true, subtree: true });

            onUnmounted(() => {
                paginationObserver.disconnect();
            });
        }
    });

</script>

<template>
    <Alert v-if="fetchError && !isLoading" variant="error" :message="`Something went wrong while loading your transactions. Please try again. If you need more help, contact eCare at <a href='mailto:${SupportEmail.eCare}'>${SupportEmail.eCare}</a>.`" :details="fetchError">
        <template #actions>
            <button class="btn btn-primary" @click="retryLastSearch">Try Again</button>
        </template>
    </Alert>

    <Spinner :show="isLoading" :label="spinnerLabel"></Spinner>
    <div v-if="!isLoading && !hasData" class="panel well empty-notice">
        <p><span class="glyphicon glyphicon-remove-circle" aria-hidden="true"></span></p>
        <h2>No payments to display.</h2>
        <p>Do you need to adjust your filters above?</p>
    </div>
    <CmnhDataGrid v-show="hasData" ref="gridRef" :columns="columns" :row-data="rowDataRef" :filter="search ?? ''" :useRemoteFiltering="true" :show-pagination-counter="true" :use-remote-paging="true" :use-remote-sorting="true" :pagination-size-selector="[5, 10, 20, 50]" :pagination-button-count="0" items-name="Payments" :pagination="20" column-layout="fitColumns" @data-loaded="handleDataLoaded"
        @data-error="(message: string) => { fetchError = message; isLoading = false }" />
</template>

<style scoped>
    :deep(.tabulator-cell[tabulator-field=dateSubmitted]) {
        flex-direction: column;
        align-items: start;
        justify-content: center;
    }

    :deep(.tabulator-cell[tabulator-field=transactionStatus]) {
        flex-direction: column;
        align-items: start;
        justify-content: center;
    }

    :deep(.timestamp-wrapper) {
        display: flex;
        flex-direction: column;
    }

    :deep(.date-value) {
        font-weight: bold;
    }

    :deep(.time-value) {
        font-size: 10px;
    }
</style>
